import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { DefaultSeo } from "@/components/SEO";
import HomePage from "./pages/HomePage";

// Lazy-load non-homepage routes to reduce initial JS bundle
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BioPage = lazy(() => import("./pages/BioPage"));
const SpeakerPage = lazy(() => import("./pages/SpeakerPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const NewLivingExpoPage = lazy(() => import("./pages/NewLivingExpoPage"));
const BookPage = lazy(() => import("./pages/BookPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const MediaRoomPage = lazy(() => import("./pages/MediaRoomPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    {/* Site defaults mount first, so each page's <SEO> mounts after them and wins every tag. */}
    <DefaultSeo />
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/bio" element={<BioPage />} />
              <Route path="/speaker" element={<SpeakerPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/new-living-expo" element={<NewLivingExpoPage />} />
              <Route path="/book" element={<BookPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/media" element={<MediaRoomPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms-of-service" element={<TermsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPage />} />
              <Route path="/returns-and-refunds" element={<ReturnsPage />} />
              <Route path="/vm" element={<AdminLoginPage />} />
              <Route path="/vm/dashboard" element={<AdminDashboardPage />} />
              {/* Redirects from old routes */}
              <Route path="/media-room" element={<Navigate to="/media" replace />} />
              <Route path="/speaking" element={<Navigate to="/events" replace />} />
              <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
